"use client";

import { createRrApiClient } from "@rr/api";
import { Locale, isValidLocale } from "@rr/utils";
import { getAuthToken } from "@/lib/auth/session";

/**
 * Returns a configured @rr/api client bound to the current admin session.
 *
 * The token is read from the existing `lib/auth/session` helpers (which
 * already manage cookie + localStorage), and we fall back to "ru" locale
 * since rr-admin doesn't currently use next-intl's locale switcher.
 *
 * Hooks created on top of this should call useApi() and use the returned
 * `api.<resource>.<method>(...)` calls inside React Query queryFn /
 * mutationFn — same pattern as tenant-web.
 */
export function useApi() {
  // Auth token is browser-only. SSR call sites just get an unauthenticated
  // client; React Query queries set `enabled` based on auth state anyway.
  const token = typeof window !== "undefined" ? getAuthToken() : null;

  // rr-admin doesn't yet expose a locale switcher; default to 'ru' to match
  // the rest of the platform's API responses.
  const locale: Locale = isValidLocale("ru") ? ("ru" as Locale) : ("ru" as Locale);

  return createRrApiClient({
    baseUrl: import.meta.env.VITE_API_URL || "/api/v1",
    token: token ?? undefined,
    locale,
  });
}
